(function () {
  const PRODUCT_LIMIT = 250;
  const MAX_PAGES = 80;
  const MAX_VARIANTS = 100000;
  const PARTIAL_TTL = 5 * 60 * 1000;
  const MAX_RESUMES = 3;
  const ALL_COLLECTIONS = "__all";
  const collectionCache = new Map();
  const partialCollectionCache = new Map();
  let collectionRetryAt = 0;
  const decoder = document.createElement("textarea");

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function decodeHtml(value) {
    decoder.innerHTML = String(value == null ? "" : value);
    return decoder.value;
  }

  function normalizeText(value) {
    return decodeHtml(value)
      .toLowerCase()
      .replace(/&/g, " and ")
      .replace(/[''`]/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim()
      .replace(/\s+/g, " ");
  }

  function normalizeCode(value) {
    return String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  }

  function unique(values) {
    return Array.from(new Set(values.filter(Boolean)));
  }

  function extractCodes(value) {
    const text = decodeHtml(value);
    const patterns = [
      /\b(?:OP|EB|ST|PRB|DON|GD|BP|BT|FB|EX)\s*[-_ ]?\s*\d{2,3}\s*[-_ ]\s*\d{2,3}[A-Z]?\b/gi,
      /\b[A-Z]{1,4}\d{2}\s*[-_ ]\s*\d{1,4}[A-Z]?\b/g,
      /\b[A-Z]{2,6}\s*[-_ ]\s*\d{1,4}[A-Z]?\b/g,
    ];
    const matches = [];

    patterns.forEach((pattern) => {
      const found = text.match(pattern);
      if (found) {
        found.forEach((match) => matches.push(normalizeCode(match)));
      }
    });

    return unique(matches);
  }

  function titleCase(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/\b([a-z])/g, (match) => match.toUpperCase());
  }

  function stripDeckAnnotations(value) {
    let cleaned = decodeHtml(value)
      .replace(/\s+\*?F\*?\s*$/i, "")
      .replace(/\s+\[(?:foil|nonfoil|non-foil|etched(?: foil)?|near mint|lightly played|moderately played|heavily played|damaged)\]\s*$/gi, "")
      .replace(/\s+\((?:[A-Z0-9]{2,6})\)\s*#?\d+[A-Z]?\s*$/i, "")
      .replace(/\s+\((?:[A-Z0-9]{2,6})\)\s+\d+[A-Z]?\s*$/i, "");

    return cleaned.replace(/\s+/g, " ").trim();
  }

  function parseDecklist(value) {
    const map = new Map();
    if (String(value || "").length > 100000) throw new Error("Decklist is too large.");
    const lines = String(value || "").split(/\r?\n/);
    if (lines.length > 500) throw new Error("Use at most 500 card lines per search.");

    lines.forEach((line) => {
      let raw = line.trim();
      if (!raw || raw.startsWith("//") || raw.startsWith("#")) return;
      if (/^(sideboard|commander|companion|main deck)$/i.test(raw)) return;

      raw = raw.replace(/^SB:\s*/i, "").trim();
      let quantity = 1;
      let name = raw;
      let match = raw.match(/^([+-]?\d+(?:\.\d+)?)\s*x?\s+(.+)$/i);

      if (match) {
        quantity = Number(match[1] || 1);
        name = match[2].trim();
      } else {
        match = raw.match(/^(.+?)\s+x\s*([+-]?\d+(?:\.\d+)?)$/i);
        if (match) {
          name = match[1].trim();
          quantity = Number(match[2] || 1);
        }
      }

      const codes = extractCodes(name);
      const printing = name.match(/\(([A-Z0-9]{2,6})\)\s*#?(\d+[A-Z]?)/i);
      validateQuantity(quantity);
      const finishMatch = name.match(/\[(non-?foil|etched(?: foil)?|foil)\]\s*$/i);
      const finish = finishMatch ? finishOf(finishMatch[1]) : /\*F\*\s*$/i.test(name) ? "foil" : null;
      const wantsFoil = finish === "foil";
      const cleanName = stripDeckAnnotations(name);
      const normalizedName = normalizeText(cleanName);

      if (!normalizedName && !codes.length) return;

      const key = [normalizedName, codes.join(","), finish || "any", printing ? printing[0].toUpperCase() : ""].join("|");
      const existing = map.get(key);
      if (existing) {
        existing.quantity += quantity;
        validateQuantity(existing.quantity);
        existing.rawLines.push(raw);
        return;
      }

      map.set(key, {
        quantity,
        finish,
        printing: printing ? [printing[1].toUpperCase(), printing[2].toUpperCase()] : null,
        name: cleanName || name,
        normalizedName,
        codes,
        wantsFoil,
        rawLines: [raw],
      });
    });

    return Array.from(map.values());
  }

  function extractProductName(product) {
    const title = decodeHtml(product.title || "");
    return title
      .replace(/(?:\s+\[(?:foil|nonfoil|non-foil|etched(?: foil)?)\])+\s*$/i, "")
      .replace(/\s+-\s+.+\s+\([A-Z0-9]{2,6}\)\s+#?\d+[A-Z]?\s*$/i, "")
      .replace(/\s+\([A-Z0-9]{2,6}\)\s+#?\d+[A-Z]?\s*$/i, "")
      .trim();
  }

  function validateQuantity(quantity) {
    if (!Number.isSafeInteger(quantity) || quantity < 1) throw new Error("Quantities must be positive whole numbers within the supported range.");
  }

  function finishOf(value) {
    if (/\bnon[ -]?foil\b/i.test(value)) return "nonfoil";
    if (/\betched\b/i.test(value)) return "etched";
    if (/\bfoil\b/i.test(value)) return "foil";
    return null;
  }

  function productImage(product) {
    if (Array.isArray(product.images) && product.images[0]?.src) return product.images[0].src;
    if (product.image?.src) return product.image.src;
    return "";
  }

  function indexProduct(product, collectionHandle) {
    return (Array.isArray(product.variants) ? product.variants : []).map((variant) => indexVariant(product, collectionHandle, variant));
  }

  function indexVariant(product, collectionHandle, variant) {
    const tagText = Array.isArray(product.tags) ? product.tags.join(" ") : "";
    const skuText = Array.isArray(product.variants) ? [variant.sku || ""].join(" ") : "";
    const productName = extractProductName(product);
    const title = decodeHtml(product.title || "");
    const codes = extractCodes([title, product.handle, skuText, tagText].join(" "));

    return {
      id: product.id,
      title,
      handle: product.handle,
      url: `/products/${product.handle}?variant=${variant.id}`,
      image: productImage(product),
      price: variant?.price || product.price || product.price_min || "",
      available: variant?.available === true,
      variantId: variant?.id || null,
      collectionHandle,
      productName,
      normalizedName: normalizeText(productName),
      normalizedTitle: normalizeText(title),
      codes,
      variantTitle: variant.title || "",
      finish: finishOf([variant.title, variant.option1, variant.option2, variant.option3].join(" ")) || finishOf((title.match(/(?:\s+\[[^\]]+\])+\s*$/) || [""])[0]) || finishOf(tagText) || "nonfoil",
    };
  }

  function retryTime(value) {
    const now = Date.now(), text = String(value || "").trim();
    const delay = /^\d+$/.test(text) ? Number(text) * 1000 : /[a-z]/i.test(text) ? Date.parse(text) - now : NaN;
    return now + (Number.isFinite(delay) ? Math.max(1000, delay) : 30000);
  }

  async function fetchCollectionPage(handle, page) {
    const url = `/collections/${encodeURIComponent(handle)}/products.json?limit=${PRODUCT_LIMIT}&page=${page}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      const error = new Error(response.status === 429 ? "Rate limited (HTTP 429)" : `Could not load ${handle}.`);
      error.status = response.status;
      error.retryAt = retryTime(response.headers?.get("Retry-After"));
      throw error;
    }

    const payload = await response.json();
    if (!Array.isArray(payload.products) || payload.products.length > PRODUCT_LIMIT) throw new Error("Invalid collection response.");
    return payload.products;
  }

  async function loadCollection(handle, pageLimit = MAX_PAGES, status, budget = { remaining: MAX_PAGES }, active = () => true, owner = handle) {
    if (!active()) throw new Error("Search cancelled.");
    for (const [key, partial] of partialCollectionCache) {
      if (Date.now() - partial.startedAt >= PARTIAL_TTL) partialCollectionCache.delete(key);
    }
    if (collectionCache.has(handle)) return collectionCache.get(handle);
    const saved = partialCollectionCache.get(owner);
    const state = saved?.handle === handle
      ? { ...saved, products: saved.products.slice(), seen: new Set(saved.seen) }
      : { handle, products: [], seen: new Set(), nextPage: 1, startedAt: Date.now(), resumes: 0 };
    const expired = () => ({ products: [], complete: false, reason: "Saved search progress expired. Submit a new search" });
    const paused = () => ({ products: state.products, complete: false, paused: true,
      reason: state.resumes >= MAX_RESUMES ? "Resume limit reached. Edit the decklist to start a new search" : state.reason || "Rate limited (HTTP 429)",
      retryAt: Math.max(state.retryAt || 0, collectionRetryAt), resumable: state.resumes < MAX_RESUMES });
    if (state.resumes >= MAX_RESUMES || Date.now() < Math.max(state.retryAt || 0, collectionRetryAt)) return paused();
    if (saved?.handle === handle) state.resumes += 1;
    const promise = (async () => {
      const { products, seen } = state;
      for (let page = state.nextPage; page <= Math.min(MAX_PAGES, pageLimit) && budget.remaining > 0; page += 1) {
        if (!active()) throw new Error("Search cancelled.");
        budget.remaining -= 1;
        if (status) status(`Loading ${titleCase(handle.replace(/-/g, " "))} singles, page ${page}...`);
        let pageProducts;
        try {
          pageProducts = await fetchCollectionPage(handle, page);
        } catch (error) {
          if (!active()) throw new Error("Search cancelled.");
          if (!products.length && error.status !== 429) throw error;
          state.reason = error.message || "Collection request failed";
          state.retryAt = error.retryAt || retryTime(null);
          if (error.status === 429) collectionRetryAt = Math.max(collectionRetryAt, state.retryAt);
          if (Date.now() - state.startedAt >= PARTIAL_TTL) return expired();
          // Only verified pages are retained; the failed page is the next explicit request.
          partialCollectionCache.set(owner, state);
          if (partialCollectionCache.size > 5) partialCollectionCache.delete(partialCollectionCache.keys().next().value);
          return paused();
        }
        if (!active()) throw new Error("Search cancelled.");
        if (Date.now() - state.startedAt >= PARTIAL_TTL) return expired();
        for (const product of pageProducts) {
          if (!product.id || !Array.isArray(product.variants)) throw new Error("Invalid product response.");
          if (seen.has(String(product.id))) return { products, complete: false, reason: "Repeated or overlapping page" };
          seen.add(String(product.id));
          if (products.length + product.variants.length > MAX_VARIANTS) return { products, complete: false, reason: "Variant limit reached" };
          products.push(...indexProduct(product, handle));
        }
        if (pageProducts.length < PRODUCT_LIMIT) return { products, complete: true };
        state.nextPage = page + 1;
      }
      return { products, complete: false, reason: "Page budget reached" };
    })();

    try {
      const result = await promise;
      if (!active()) throw new Error("Search cancelled.");
      if (!result.paused && partialCollectionCache.get(owner) === saved) partialCollectionCache.delete(owner);
      if (result.complete) collectionCache.set(handle, result);
      // Keep the aggregate cache bounded as well as each traversal.
      if (collectionCache.size > 5) collectionCache.delete(collectionCache.keys().next().value);
      return result;
    } catch (error) {
      if (active() && partialCollectionCache.get(owner) === saved) partialCollectionCache.delete(owner);
      throw error;
    }
  }

  async function loadProducts(root, selectedHandle, pageLimit = MAX_PAGES, status, active = () => true, owner = root) {
    const select = root.querySelector("[data-pulse-decklist-game]");
    const handles = unique(Array.from(select?.options || []).map((option) => option.value).filter((value) => value && value !== ALL_COLLECTIONS));
    const targetHandles = selectedHandle === ALL_COLLECTIONS ? handles : [selectedHandle];
    const products = [], budget = { remaining: MAX_PAGES };
    let complete = true, reason = "", retryAt = 0, resumable = false;
    for (const handle of targetHandles) {
      const batch = await loadCollection(handle, pageLimit, status, budget, active, owner);
      const room = MAX_VARIANTS - products.length;
      products.push(...batch.products.slice(0, room));
      complete = complete && batch.complete && batch.products.length <= room;
      if (!batch.complete) reason = batch.reason;
      if (batch.paused) {
        retryAt = batch.retryAt;
        resumable = batch.resumable;
        break;
      }
    }
    return { products, complete, reason, retryAt, resumable };
  }

  function scoreProduct(item, product) {
    if (item.finish && item.finish !== product.finish) return 0;
    if (item.printing) {
      const printing = product.title.match(/\(([A-Z0-9]{2,6})\)\s*#?(\d+[A-Z]?)/i);
      if (!printing || printing[1].toUpperCase() !== item.printing[0] || printing[2].toUpperCase() !== item.printing[1]) return 0;
    }
    if (item.codes.length && !item.codes.every((code) => product.codes.includes(code))) return 0;
    if (item.normalizedName !== product.normalizedName && item.normalizedName !== product.normalizedTitle && !item.codes.includes(normalizeCode(item.name))) return 0;
    return 150 + Number(product.available);
  }

  function findMatches(items, products) {
    return items.map((item) => {
      const scored = products
        .map((product) => ({ product, score: scoreProduct(item, product) }))
        .filter((entry) => entry.score >= (item.codes.length ? 120 : 70))
        .sort((a, b) => b.score - a.score || Number(b.product.available) - Number(a.product.available));

      const seen = new Set();
      const matches = [];
      scored.forEach((entry) => {
        const key = entry.product.variantId || entry.product.handle;
        if (seen.has(key)) return;
        seen.add(key);
        matches.push(entry);
      });

      return {
        ...item,
        matches: matches.slice(0, 6),
      };
    });
  }

  function formatPrice(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "";
    return amount.toLocaleString(undefined, { style: "currency", currency: "USD" });
  }

  function renderProductMatch(entry, quantity) {
    const product = entry.product;
    const addDisabled = !product.available || !product.variantId;

    return `
      <article class="pulse-decklist__match">
        <a class="pulse-decklist__match-image" href="${escapeHtml(product.url)}">
          ${
            product.image
              ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}">`
              : `<div class="pulse-product-card__media-fallback">${escapeHtml(product.productName || product.title)}</div>`
          }
        </a>
        <div class="pulse-decklist__match-body">
          <a class="pulse-decklist__match-title" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
          <div class="pulse-decklist__match-meta">
            ${product.variantTitle ? `<span>${escapeHtml(product.variantTitle)}</span>` : ""}
            ${product.price ? `<strong>${escapeHtml(formatPrice(product.price))}</strong>` : ""}
            <span>${product.available ? "In stock" : "Unavailable"}</span>
          </div>
          <button
            type="button"
            class="pulse-button pulse-button--ghost pulse-decklist__add"
            data-pulse-decklist-add
            data-variant-id="${escapeHtml(product.variantId || "")}"
            data-quantity="${escapeHtml(quantity)}"
            ${addDisabled ? "disabled" : ""}
          >
            ${addDisabled ? "Unavailable" : `Add ${escapeHtml(quantity)}`}
          </button>
        </div>
      </article>
    `;
  }

  function renderResults(root, results, complete = true) {
    const resultsEl = root.querySelector("[data-pulse-decklist-results]");
    const visibleItems = results
      .map((item) => ({
        ...item,
        availableMatches: item.matches.filter((entry) => entry.product.available && entry.product.variantId),
      }))
      .filter((item) => item.availableMatches.length);
    const hiddenCount = Math.max(0, results.length - visibleItems.length);
    const addable = visibleItems.map((item) => ({ item, best: item.availableMatches[0] })).filter((entry) => entry.best);

    root.pulseDecklistAddAll = addable.map((entry) => ({
      id: entry.best.product.variantId,
      quantity: entry.item.quantity,
    }));

    if (!resultsEl) {
      return { available: visibleItems.length, hidden: hiddenCount, total: results.length };
    }

    resultsEl.innerHTML = `
      <div class="pulse-decklist__summary">
        <div>
          <strong>${visibleItems.length} available ${visibleItems.length === 1 ? "card" : "cards"} found</strong>
          <span>${complete ? `${hiddenCount} lines have no available exact match in the searched catalog.` : `Search incomplete. ${hiddenCount} lines remain unresolved; more matches may exist.`}</span>
        </div>
        <div class="pulse-decklist__summary-actions">
          <button type="button" class="pulse-button pulse-button--primary" data-pulse-decklist-add-all ${addable.length ? "" : "disabled"}>
            Add found cards to cart
          </button>
          <a class="pulse-button pulse-button--ghost" href="/cart">View cart</a>
        </div>
      </div>

      ${
        visibleItems.length
          ? `<div class="pulse-decklist__items">
              ${visibleItems
                .map((item) => {
                  const matchCount = item.availableMatches.length;
                  return `
                    <article class="pulse-decklist__item is-matched">
                      <div class="pulse-decklist__item-head">
                        <div>
                          <p class="pulse-product-card__kicker">${escapeHtml(item.quantity)} requested</p>
                          <h3>${escapeHtml(item.name)}</h3>
                          ${item.codes.length ? `<p class="pulse-decklist__codes">${escapeHtml(item.codes.join(", "))}</p>` : ""}
                        </div>
                        <span class="pulse-decklist__pill">${matchCount} in-stock ${matchCount === 1 ? "match" : "matches"}</span>
                      </div>
                      <div class="pulse-decklist__matches">
                        ${item.availableMatches.map((entry) => renderProductMatch(entry, item.quantity)).join("")}
                      </div>
                    </article>
                  `;
                })
                .join("")}
            </div>`
          : `<div class="pulse-decklist__item is-missing">
              <div class="pulse-decklist__item-head">
                <div>
                  <p class="pulse-product-card__kicker">${complete ? "No available exact matches" : "Search incomplete"}</p>
                  <h3>${complete ? "No available exact matches were found in the searched catalog." : "Some catalog pages could not be searched. Unmatched cards remain unresolved."}</h3>
                  <p class="pulse-decklist__missing">Try adding set codes or collector numbers, or check back after the next singles upload.</p>
                </div>
              </div>
            </div>`
      }
    `;

    return { available: visibleItems.length, hidden: hiddenCount, total: results.length };
  }
  function setStatus(root, message, tone) {
    const status = root.querySelector("[data-pulse-decklist-status]");
    if (!status) return;
    status.textContent = message || "";
    status.dataset.tone = tone || "";
  }

  async function addItemsToCart(items) {
    const combined = new Map();
    for (const item of items) {
      validateQuantity(item.quantity);
      if (!/^\d+$/.test(String(item.id)) || Number(item.id) < 1) throw new Error("Invalid variant.");
      const quantity = (combined.get(String(item.id)) || 0) + item.quantity;
      validateQuantity(quantity);
      combined.set(String(item.id), quantity);
    }
    items = Array.from(combined, ([id, quantity]) => ({ id, quantity }));
    const cartResponse = await fetch("/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } });
    if (!cartResponse.ok) throw new Error("Could not check the current cart. Please retry.");
    const cart = await cartResponse.json();
    if (!Array.isArray(cart.items)) throw new Error("Could not check the current cart.");
    for (const item of items) {
      const existing = cart.items.filter((line) => String(line.variant_id || line.id) === String(item.id)).reduce((sum, line) => sum + Number(line.quantity), 0);
      validateQuantity(existing + item.quantity);
      // Public schemas may omit inventory. Only explicit tracked, deny-policy stock is a limit.
      const variantResponse = await fetch(`/variants/${item.id}.js`, { credentials: "same-origin", headers: { Accept: "application/json" } }).catch(() => null);
      const variant = variantResponse?.ok ? await variantResponse.json().catch(() => null) : null;
      if (variant && String(variant.id) === String(item.id) && variant.inventory_management && variant.inventory_policy === "deny" && Number.isInteger(variant.inventory_quantity) && existing + item.quantity > variant.inventory_quantity) {
        throw new Error("Requested quantity exceeds available stock, including cards already in your cart.");
      }
    }
    const response = await fetch(window.routes?.cart_add_url || "/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || "Cart add failed.");
    }

    return response.json();
  }

  function initDecklist(root) {
    const form = root.querySelector("[data-pulse-decklist-form]");
    const input = root.querySelector("[data-pulse-decklist-input]");
    const select = root.querySelector("[data-pulse-decklist-game]");
    const pageLimit = MAX_PAGES;

    if (!form || !input || !select) return;

    let generation = 0, owner = {}, searching = false;
    const submit = form.querySelector('button[type="submit"]');
    const originalText = submit?.textContent || "Find matches";
    function invalidate(discardPartial = true) {
      generation += 1;
      searching = false;
      if (discardPartial) { partialCollectionCache.delete(owner); owner = {}; }
      const resultsEl = root.querySelector("[data-pulse-decklist-results]");
      if (resultsEl) resultsEl.innerHTML = "";
      root.pulseDecklistAddAll = [];
      setStatus(root, "", "");
      if (submit) { submit.disabled = false; submit.textContent = originalText; }
    }
    input.addEventListener("input", invalidate);
    select.addEventListener("change", invalidate);
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (searching && owner.value === input.value && owner.handle === select.value) return;
      invalidate(owner.value !== input.value || owner.handle !== select.value);
      const token = generation, value = input.value, handle = select.value;
      owner.value = value;
      owner.handle = handle;
      const active = () => token === generation && value === input.value && handle === select.value;
      let resumable = false;
      try {
        const deckItems = parseDecklist(value);
        if (!deckItems.length) throw new Error("Paste at least one card line first.");
        searching = true;
        if (submit) { submit.disabled = true; submit.textContent = "Searching..."; }
        const catalog = await loadProducts(root, handle, pageLimit, (message) => { if (active()) setStatus(root, message, ""); }, active, owner);
        if (!active()) return;
        resumable = catalog.resumable;
        const summary = renderResults(root, findMatches(deckItems, catalog.products), catalog.complete);
        const wait = Math.max(0, Math.ceil((catalog.retryAt - Date.now()) / 1000));
        const recovery = resumable ? ` ${wait ? `Wait ${wait} seconds, then choose` : "Choose"} Resume search to continue.` : "";
        setStatus(root, catalog.complete
          ? `Search complete. Found ${summary.available} available exact card matches.`
          : `Search INCOMPLETE: ${catalog.reason || "resource limit or repeated page"}. Found ${summary.available} available matches; other cards remain unresolved.${recovery}`, catalog.complete ? "success" : "warning");
      } catch (error) {
        if (!active()) return;
        invalidate();
        setStatus(root, error.message || "Decklist search failed. Please try again.", "error");
      } finally {
        if (active()) {
          searching = false;
          if (submit) { submit.disabled = false; submit.textContent = resumable ? "Resume search" : originalText; }
        }
      }
    });

    root.addEventListener("click", async (event) => {
      const singleAdd = event.target.closest("[data-pulse-decklist-add]");
      const addAll = event.target.closest("[data-pulse-decklist-add-all]");
      if (!singleAdd && !addAll) return;

      const button = singleAdd || addAll;
      if (button.disabled) return;
      const clickGeneration = generation;
      const originalText = button.textContent;
      let items = [];

      if (singleAdd) {
        const id = singleAdd.getAttribute("data-variant-id");
        const quantity = Number(singleAdd.getAttribute("data-quantity"));
        if (!id) return;
        items = [{ id, quantity }];
      } else {
        items = Array.isArray(root.pulseDecklistAddAll) ? root.pulseDecklistAddAll : [];
      }

      if (!items.length) return;

      button.disabled = true;
      button.textContent = "Adding...";

      try {
        await addItemsToCart(items);
        if (clickGeneration !== generation) return;
        button.textContent = "Added";
        setStatus(root, `${items.length} line${items.length === 1 ? "" : "s"} added to cart.`, "success");
      } catch (error) {
        if (clickGeneration !== generation) return;
        console.error("[Pulse v2 decklist] Cart add failed.", error);
        button.textContent = "Could not add";
        setStatus(root, error.message || "One or more cards could not be added.", "error");
      } finally {
        window.setTimeout(() => {
          button.disabled = false;
          button.textContent = originalText;
        }, 1600);
      }
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("[data-pulse-decklist-builder]").forEach(initDecklist);
  });
})();
